import * as React from 'react';
import { Dialog, DialogFooter, DirectionalHint, Dropdown, IconButton, IDropdownOption, Label, PrimaryButton, Stack, TextField } from '@fluentui/react';
import { IStackMarker } from '../../../../models/Maps/IStackMarker';
import { Direction } from 'leaflet';
import { isMobileOrTablet } from '../../../VisibilityControl/UserAgentControl';
import { LanguageContext } from '../../../../LanguageContext';
import { getTreeSpeciesText } from './stackDialogOptions';

export interface IStackDialogProps {
    currentStack: IStackMarker;
    onSaveClicked(identifier: string, no: string, size: number, treeSpecies: string, tooltipPosition: Direction): void;
    onChanged?: (no: string, size: number, treeSpecices: string, tooltipPosition: string) => void;
    onDismiss?: () => void;
}

export interface IStackDialogState {
    no: string; //must be number, so will keep as string to ensure previously created stacks (backward comp.)
    size: number;
    treeSpecices: string;
    tooltipPosition: string;
}

export default class StackDialog extends React.Component<IStackDialogProps, IStackDialogState> {
    static contextType = LanguageContext;
    context: React.ContextType<typeof LanguageContext>;

    private t(key: string): string {
        return this.context?.t ? this.context.t(key) : key;
    }

    constructor(props: IStackDialogProps) {
        super(props);

        this.state = {
            no: this.props.currentStack.no ? this.props.currentStack.no : "",
            size: this.props.currentStack.size ? this.props.currentStack.size : 0,
            treeSpecices: this.props.currentStack.treeSpecices ? this.props.currentStack.treeSpecices : "",
            tooltipPosition: this.props.currentStack.tooltipPosition ? this.props.currentStack.tooltipPosition : "top"
        };
    }

    private renderActionButton() {
        return (
            <PrimaryButton
                style={{ width: '100%', marginBottom: 10, marginTop: -10 }}
                text={this.t('stackDialog.save')}
                onClick={() => {
                    this.props.onSaveClicked(
                        this.props.currentStack.identifier,
                        this.state.no,
                        this.state.size,
                        this.state.treeSpecices,
                        this.state.tooltipPosition as Direction
                    );
                }}
            />
        );
    }

    public render(): React.ReactElement<IStackDialogProps> {
        const t = this.t.bind(this);

        return (
            <Dialog
                hidden={false}
                minWidth={200}
                onDismiss={this.props.onDismiss}
                modalProps={{ isBlocking: true }}
                dialogContentProps={{
                    title: (
                        <Stack
                            horizontal
                            horizontalAlign="space-between"
                            verticalAlign="center"
                            style={{ marginBottom: -10 }}
                        >
                            <Label style={{ fontSize: '16px' }}> {t('stackDialog.title')} </Label>
                            <IconButton
                                iconProps={{ iconName: 'Cancel' }}
                                ariaLabel={t('stackDialog.close')}
                                onClick={this.props.onDismiss}
                                styles={{ root: { marginRight: -30, marginTop: 5 } }}
                            />
                        </Stack>
                    )
                }}
            >
                {isMobileOrTablet() && (
                    <div style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        paddingTop: -30,
                        marginTop: 0,
                    }}>
                        {this.renderActionButton()}
                    </div>
                )}
                <TextField
                    label={t('stackDialog.stackNumber')}
                    pattern="[0-9]*"
                    description={t('stackDialog.stackNumberError')}
                    value={this.state.no}
                    onChange={(ev, newValue) => {
                        let v = newValue ? newValue : "0";
                        if ((ev.target as any).validity.valid) {
                            this.setState({ no: parseFloat(v + "") + "" }, () => {
                                if (this.props.onChanged) {
                                    this.props.onChanged(
                                        this.state.no,
                                        this.state.size,
                                        this.state.treeSpecices,
                                        this.state.tooltipPosition
                                    );
                                }
                            });
                        }
                    }}
                />
                <TextField
                    label={t('stackDialog.stackSize')}
                    pattern="[0-9]*"
                    description={t('stackDialog.stackSizeError')}
                    value={this.state.size + ""}
                    suffix={t('common.rm')}
                    onChange={(ev, newValue) => {
                        let v = newValue ? newValue : "0";

                        if ((ev.target as any).validity.valid) {
                            this.setState({ size: parseFloat(v + "") }, () => {
                                if (this.props.onChanged) {
                                    this.props.onChanged(
                                        this.state.no,
                                        this.state.size,
                                        this.state.treeSpecices,
                                        this.state.tooltipPosition
                                    );
                                }
                            });
                        }
                    }}
                />
                <Dropdown
                    placeholder={t('common.selectFromList')}
                    label={t('stackDialog.treeType')}
                    selectedKey={this.state.treeSpecices}
                    options={[
                        { key: 'Løv', text: getTreeSpeciesText('Løv', t) },
                        { key: 'Nål', text: getTreeSpeciesText('Nål', t) },
                        { key: 'Blandet', text: getTreeSpeciesText('Blandet', t) },
                        { key: 'Pil/poppel', text: getTreeSpeciesText('Pil/poppel', t) }
                    ]}
                    onChange={(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
                        this.setState({ treeSpecices: option?.key + "" }, () => {
                            if (this.props.onChanged) {
                                this.props.onChanged(
                                    this.state.no,
                                    this.state.size,
                                    this.state.treeSpecices,
                                    this.state.tooltipPosition
                                );
                            }
                        });
                    }}
                />
                <Dropdown
                    placeholder={t('common.selectFromList')}
                    label={t('stackDialog.textPlacement')}
                    selectedKey={this.state.tooltipPosition}
                    options={[
                        { key: 'top', text: t('stackDialog.top') },
                        { key: 'bottom', text: t('stackDialog.bottom') },
                        { key: 'left', text: t('stackDialog.left') },
                        { key: 'right', text: t('stackDialog.right') }
                    ]}
                    onChange={(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => { 
                        this.setState({ tooltipPosition: option?.key + "" }, () => {
                            if (this.props.onChanged) {
                                this.props.onChanged(
                                    this.state.no,
                                    this.state.size,
                                    this.state.treeSpecices,
                                    this.state.tooltipPosition
                                );
                            }
                        });
                    }}
                />
                {!isMobileOrTablet() &&
                    <DialogFooter>
                        {this.renderActionButton()}
                    </DialogFooter>
                }
            </Dialog>);
    }
}